/**
 * useReactiveNoteShareInfoMap - Reactive share-info map for owned notes
 *
 * Subscribes to the Apollo cache (via `apolloClient.watchQuery`) for each
 * owned note's `listNoteShares` query, so cache writes performed elsewhere
 * (e.g. `cache.modify` from share/revoke mutations) instantly update the
 * returned share-count map without imperative refetches (SE-4289).
 *
 * Manager auto-shares are filtered out of the count to match the viewer-
 * facing definition of "manually shared" (SE-3990).
 * @module features/notes/hooks/useReactiveNoteShareInfoMap
 */

import { useApolloClient } from "@apollo/client";
import { useEffect, useState } from "react";

import {
  NotesListNoteSharesDocument,
  type NotesListNoteSharesQuery,
  type NotesListNoteSharesQueryVariables,
} from "@/generated/graphql";

/** Per-note share metadata used by the notes section UI */
export interface NoteShareInfo {
  /** Number of users this note is manually shared with (excluding owner + auto-shared managers) */
  readonly shareCount: number;
  /** Whether the current viewer received this note via a share */
  readonly isSharedWithMe: boolean;
}

/**
 * Stable string key derived from a Set of ids. The set's iteration order is
 * insertion order, which can flap across renders; sorting + joining gives
 * useEffect a deterministic dependency that only changes when the membership
 * actually changes.
 * @param ids - Set of note ids
 * @returns Stable comma-joined sorted key
 */
const setToStableKey = (ids: ReadonlySet<string>): string =>
  [...ids].sort((a, b) => a.localeCompare(b)).join(",");

/**
 * Subscribes to `listNoteShares` for each owned note and returns a reactive
 * map of share counts. Filters OWNER role and any user IDs in `managerIdSet`
 * out of the count (manager auto-shares are not viewer-visible).
 * @param ownedNoteIds - Set of note ids the current user owns
 * @param managerIdSet - Set of org manager/admin user ids to filter from counts
 * @param enabled - When false, returns an empty map (no subscriptions)
 * @returns A map of noteId -> NoteShareInfo, kept in sync with the cache
 */
export const useReactiveNoteShareInfoMap = (
  ownedNoteIds: ReadonlySet<string>,
  managerIdSet: ReadonlySet<string>,
  enabled: boolean
): Record<string, NoteShareInfo> => {
  const apolloClient = useApolloClient();
  const [map, setMap] = useState<Record<string, NoteShareInfo>>({});

  const ownedKey = setToStableKey(ownedNoteIds);
  const managerKey = setToStableKey(managerIdSet);

  useEffect(() => {
    if (!enabled || ownedNoteIds.size === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- effect must reset the share map when the user disables sharing or empties the owned-set; this is the canonical "reset external-driven state" path.
      setMap({});
      return;
    }

    // Drop entries for notes no longer owned to avoid stale counts.
    // When ownedNoteIds shrinks (e.g. a note is deleted or unshared),
    // the previous map still holds the old entry. Without this prune,
    // consumers would see outdated share counts for notes the user no
    // longer owns.

    setMap(prev =>
      [...ownedNoteIds].reduce<Record<string, NoteShareInfo>>((acc, noteId) => {
        const existing = prev[noteId];
        return existing ? { ...acc, [noteId]: existing } : acc;
      }, {})
    );

    const subscriptions = [...ownedNoteIds].map(noteId => {
      const observable = apolloClient.watchQuery<
        NotesListNoteSharesQuery,
        NotesListNoteSharesQueryVariables
      >({
        query: NotesListNoteSharesDocument,
        variables: { noteId },
        fetchPolicy: "cache-first",
      });

      return observable.subscribe({
        next: ({ data }) => {
          const shares = data?.listNoteShares ?? [];
          const manualShares = shares.filter(
            share => share.role !== "OWNER" && !managerIdSet.has(share.userId)
          );
          setMap(prev => {
            // Prune entries whose noteId is no longer in ownedNoteIds so
            // stale keys don't accumulate when the owned set shrinks.
            const pruned = Object.fromEntries(
              Object.entries(prev).filter(([id]) => ownedNoteIds.has(id))
            );
            return {
              ...pruned,
              [noteId]: {
                shareCount: manualShares.length,
                isSharedWithMe: false,
              },
            };
          });
        },
        error: () => {
          // Swallow — noisy cache subscription errors should not crash the page
        },
      });
    });

    return () => {
      subscriptions.forEach(sub => sub.unsubscribe());
    };
    // ownedKey/managerKey collapse the Sets to stable strings so this effect
    // only re-subscribes when membership actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: effect keyed off stable Set digests
  }, [apolloClient, enabled, ownedKey, managerKey]);

  return map;
};
